import { AddCourseModelForEdit, CombinedCourseModel, CourseModel, GroupedCourseModel } from './../models/course.model';
import { catchError, map, tap } from 'rxjs/operators';
import { ContractInfo, LessonPrices, TutorSearch } from './../microservice-clients/user';
import { UtilityService } from '../services';
import { UserRole } from '../constants'
import { HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { ApiService } from './api.service';
import { Injectable } from '@angular/core';
import { FindTutorsRequest, FindTutorsResponse, UserObj } from '../microservice-clients/user';
import { StudentTutorRelationStatus, BypassAPIGlobalHandleEnums } from '../constants';
import { SearchSchoolType } from '../models/search-school.model';
import { AddOrEditSchoolType } from '../models/add-or-edit-school.model';
import { PendingReviewsResponse, ReviewModel, TutorReviewsResponse } from '../models/review.model';
import { TopTutorBadgeResponse, TopTutorScoreResponse } from '../models/top-tutor-score.model';

let cache = {};

@Injectable({
  providedIn: 'root'
})
export class UserClientService {

  constructor(
    private apiService: ApiService,
    private utilityService: UtilityService
  ) { }

  registerUser(registrationData: any) {// TODO: add type
    const headers = this.utilityService.addHeaders(BypassAPIGlobalHandleEnums.ErrorHandle);
    return this.apiService.postWithHeaders(`user/new`, registrationData, headers)
  }

  findTutors(findTutorsRequest: FindTutorsRequest): Observable<FindTutorsResponse> {
    let params = new HttpParams()
      .set('lessonType', findTutorsRequest.lessonType)
      .set('country', findTutorsRequest.country)
      .set('limit', findTutorsRequest.limit)
      .set('offset', findTutorsRequest.offset);

    if (findTutorsRequest.schoolTypeId) {
      params = params.append('schoolTypeId', findTutorsRequest.schoolTypeId.toString())
    }

    if (findTutorsRequest.courseIds?.length) {
      params = params.append('courseIds', findTutorsRequest.courseIds.join())
    }

    if (findTutorsRequest.latitude && findTutorsRequest.longitude) {
      params = params.append('latitude', findTutorsRequest.latitude);
      params = params.append('longitude', findTutorsRequest.longitude);
    }

    if (findTutorsRequest.randomSeed) {
      params = params.append('randomSeed', findTutorsRequest.randomSeed);
    }

    if (findTutorsRequest.schoolLevelId) {
      params = params.append('schoolLevelId', findTutorsRequest.schoolLevelId);
    }

    if (findTutorsRequest.schoolYearId) {
      params = params.append('schoolYearId', findTutorsRequest.schoolYearId);
    }

    if (findTutorsRequest.anonymousId) {
      params = params.append('anonymousId', findTutorsRequest.anonymousId);
    }

    if (findTutorsRequest.tutorLevels?.length) {
      findTutorsRequest.tutorLevels.forEach(level => {
        params = params.append('tutorLevels', level);
      });
    }

    return this.apiService.get('user/tutor/find', params)
  }

  getUser(userId: string): Observable<UserObj> {
    return this.apiService.get(`user/${userId}`).pipe(catchError(error => this.handle404(error)));
  }

  getLessonPrices(tutorId: number, studentId?: number, latitude?: number, longitude?: number, durationInMinutes?: number): Observable<LessonPrices> {
    let params = new HttpParams().set('tutorId', tutorId);

    if (studentId) params = params.append('studentId', studentId);
    if (latitude) params = params.append('latitude', latitude);
    if (longitude) params = params.append('longitude', longitude);
    if (durationInMinutes) params = params.append('durationInMinutes', durationInMinutes);

    return this.apiService.get('user/prices/lesson', params);
  }

  sendMessageToTutor(message: string, tutorId: number, searchId?: string, position?: number): Observable<any> {
    const body: any = { message, tutor: tutorId };
    if (searchId) body.searchId = searchId;
    if (position) body.position = position;
    return this.apiService.post('message/tutor', body);
  }

  trackTutorCardClick(tutorId: number, searchId: string, position: number): Observable<any> {
    const headers = this.utilityService.addHeaders(BypassAPIGlobalHandleEnums.All);
    return this.apiService.postWithHeaders('api/tools/analytics/tutor-profile/card-clicked', {
      country: this.utilityService.country,
      tutorId,
      searchId,
      position,
    }, headers);
  }

  getTutorGroupedCourses(tutorId: number): Observable<Array<GroupedCourseModel>> {
    return this.apiService.get(`user/tutor/${tutorId}/courses/grouped`)
      .pipe(
        catchError(error => this.handle404(error)),
        map(response => response.courses)
      );
  }

  getTutorContractInfo(tutorId: number): Observable<ContractInfo> {
    return this.apiService.get(`user/${tutorId}/contract-info`)
      .pipe(
        catchError(error => this.handle404(error)),
        map(response => response.contractInfo)
      );
  }

  getTutorRequestedGroupedCourses(tutorId: number): Observable<{ courses: Array<GroupedCourseModel> }> {
    return this.apiService.get(`user/tutor/${tutorId}/requested-courses/grouped`)
      .pipe(
        catchError(error => this.handle404(error)),
        tap(
          (response) => {
            return {
              courses: response?.courses.map(course => {
                let _course = course;
                _course._isRequested = true;
                return _course
              })
            }
          })
      )
  }

  getTutorCombinedCourses(tutorId: string): Observable<Array<CombinedCourseModel>> {
    return this.apiService.get(`user/tutor/${tutorId}/courses/all/grouped`)
      .pipe(
        catchError(error => this.handle404(error)),
        map(response => {
          if (response.courses) {
            response.courses.map(course => {
              let _course = course;
              if (!_course.approved) {
                _course.approved = false;
              }
              return _course
            });
          }
          return response?.courses ? response.courses : [];
        })
      );
  }

  addTutorCourse(course: AddCourseModelForEdit) {
    return this.apiService.post('user/tutor/courses/new', course)

  }

  deleteTutorCourse(model: CombinedCourseModel) {
    return this.apiService.post(`user/tutor/courses/delete`, model)
  }

  getAllCourses(): Observable<Array<CourseModel>> {
    return this.apiService.get(`subject/courses/${this.utilityService.country}`)
      .pipe(
        catchError(error => this.handle404(error)),
        map(response => response.courses)
      );
  }

  getAllSearchSchoolTypesAndCourses(): Observable<{ schoolTypes: Array<SearchSchoolType> }> {
    return this.apiService.get(`subject/search/criteria/${this.utilityService.country}`);
  }

  getDefaultSchoolTypesAndCourses(): Observable<{ schoolTypes: Array<AddOrEditSchoolType> }> {
    const path = `subject/search/subjects/${this.utilityService.country}`
    return cache.hasOwnProperty(path) ? cache[path] : this.apiService.get(path).pipe(tap(response =>
      cache[path] = of(response)));
  }

  getUserRelationStatus(studentID: number, tutorID: number) {
    return this.apiService.get(`user/relations/${studentID}/${tutorID}`)
      .pipe(
        catchError(error => this.handle404(error)),
        tap((response) => {
          return (!response.status || response.status == StudentTutorRelationStatus.unknown) ? StudentTutorRelationStatus.none : response.status;
        })
      );
  }

  markSalesNoteSeen(studentId: number, tutorId: number) {
    return this.apiService.put(`user/relations/${studentId}/${tutorId}/note-seen`, {});
  }

  getFavoriteTutors(): Observable<Array<TutorSearch>> {
    return this.apiService.get(`user/favorites`)
      .pipe(
        catchError(error => this.handle404(error)),
        map(response => response.favorites)
      );
  }

  getHeardAboutUsOptions(role: UserRole): Observable<any> {
    const params = 'role=' + role + '&country=' + this.utilityService.country;
    return this.apiService.get(`user/heard/about/options?${params}`);
  }

  addFavoriteTutor(tutorId: string): Observable<TutorSearch> {
    return this.apiService.post(`user/favorites/${tutorId}`)
      .pipe(catchError(error => this.handle404(error)));
  }

  removeFavoriteTutor(tutor_id: number): Observable<TutorSearch> {
    return this.apiService.delete(`user/favorites/${tutor_id}`)
      .pipe(catchError(error => this.handle404(error)));
  }

  postReview(review: ReviewModel): Observable<ReviewModel> {
    return this.apiService.post(`user/reviews`, review)
      .pipe(catchError(error => this.handle404(error)));
  }

  getPendingReviews(): Observable<PendingReviewsResponse> {
    const headers = this.utilityService.addHeaders(BypassAPIGlobalHandleEnums.All);
    return this.apiService.getWithHeaders('user/reviews/pending', headers);
  }

  dismissPendingReview(pendingReviewId: string): Observable<void> {
    const headers = this.utilityService.addHeaders(BypassAPIGlobalHandleEnums.All);
    return this.apiService.postWithHeaders(`user/reviews/pending/${pendingReviewId}/dismiss`, {}, headers);
  }

  checkIfReviewExists(studentId: string, tutorId: string) {
    return this.apiService.get(`user/reviews/${studentId}/${tutorId}/exists`)
      .pipe(catchError(error => this.handle404(error)));
  }

  getTutorReviews(limit: number, offset: number, tutorId?: string): Observable<TutorReviewsResponse> {
    let params = new HttpParams().set('limit', limit).set('offset', offset);
    if (tutorId) {
      params = params.set('tutorId', tutorId);
    }
    return this.apiService.get('user/tutor/reviews', params)
      .pipe(catchError(error => this.handle404(error)));
  }

  getTopTutorScore(): Observable<TopTutorScoreResponse> {
    return this.apiService.get('user/tutor/top-tutor-score')
      .pipe(catchError(error => this.handle404(error)));
  }

  getTopTutorBadge(tutorId: string): Observable<TopTutorBadgeResponse> {
    return this.apiService.get(`user/tutor/${tutorId}/top-tutor-badge`)
      .pipe(catchError(error => this.handle404(error)));
  }

  private handle404(error) {
    if (error.status == 404) return of(null);
    return throwError(error);
  }

}
